import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export const MaterialsComparator = () => {
    const [materials, setMaterials] = useState([]);
    const [stores, setStores] = useState([]);
    const [disclaimer, setDisclaimer] = useState("");
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState("Tous");
    const [qty, setQty] = useState({});
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [quoteForm, setQuoteForm] = useState({ full_name: "", email: "", phone: "" });
    const [quoteBusy, setQuoteBusy] = useState(false);

    useEffect(() => {
        axios.get(`${API}/materials`).then((r) => {
            setMaterials(r.data.materials || []);
            setStores(r.data.stores || []);
            setDisclaimer(r.data.disclaimer || "");
        }).catch(() => {});
    }, []);

    const categories = useMemo(() => ["Tous", ...new Set(materials.map((m) => m.category))], [materials]);

    const filtered = useMemo(() => materials.filter((m) =>
        (cat === "Tous" || m.category === cat) &&
        m.name.toLowerCase().includes(search.toLowerCase())
    ), [materials, cat, search]);

    const totals = useMemo(() => {
        const t = {};
        stores.forEach((s) => { t[s.key] = 0; });
        let count = 0;
        materials.forEach((m) => {
            const q = Number(qty[m.id]) || 0;
            if (q > 0) {
                count += 1;
                stores.forEach((s) => { t[s.key] += q * m.prices[s.key]; });
            }
        });
        return { t, count };
    }, [qty, materials, stores]);

    const bestStore = useMemo(() => {
        if (totals.count === 0) return null;
        return stores.reduce((a, b) => (totals.t[a.key] <= totals.t[b.key] ? a : b));
    }, [totals, stores]);

    const submitQuoteRequest = async (e) => {
        e.preventDefault();
        setQuoteBusy(true);
        const lines = materials
            .filter((m) => (Number(qty[m.id]) || 0) > 0)
            .map((m) => `- ${m.name} × ${qty[m.id]} ${m.unit}(s)`);
        const totalsTxt = stores.map((s) => `${s.label} : ${fmt(totals.t[s.key])} €`).join(" · ");
        const message = `Demande de pose de matériaux via le comparateur CMRENOV AI.\n\nPanier :\n${lines.join("\n")}\n\nBudget matériaux estimé : ${totalsTxt}${bestStore ? `\nEnseigne la moins chère : ${bestStore.label}` : ""}\n\nMerci de me recontacter pour un devis pose + fourniture.`;
        try {
            await axios.post(`${API}/contact`, {
                ...quoteForm,
                subject: "Pose de matériaux — demande de devis (comparateur)",
                message,
            });
            toast.success("Demande envoyée — CM RENOV vous recontacte sous 24h ✓");
            setQuoteOpen(false);
            setQuoteForm({ full_name: "", email: "", phone: "" });
        } catch {
            toast.error("Erreur d'envoi. Réessayez.");
        } finally {
            setQuoteBusy(false);
        }
    };

    return (
        <div data-testid="materials-comparator" className="mt-16 lux-card p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                    <div className="tracking-luxe text-[#B89C6A] mb-3">🛒 Comparateur de matériaux</div>
                    <h2 className="font-serif-lux text-3xl leading-tight mb-4">
                        Leroy Merlin · Castorama · Brico Dépôt — <em className="italic text-[#B89C6A]">au meilleur prix</em>.
                    </h2>
                    <p className="text-[13px] text-[#1C1E1D]/70 leading-relaxed">
                        Sélectionnez vos matériaux, indiquez les quantités : nous comparons le budget total dans les 3 enseignes et vous indiquons la moins chère.
                    </p>
                    <p className="mt-3 text-[11px] text-[#1C1E1D]/40 italic">{disclaimer}</p>

                    {totals.count > 0 && (
                        <div data-testid="materials-totals" className="mt-6 bg-[#1C1E1D] text-[#FAF9F6] p-5">
                            <div className="tracking-luxe text-[#B89C6A] mb-3">Votre budget · {totals.count} article{totals.count > 1 ? "s" : ""}</div>
                            {stores.map((s) => (
                                <div key={s.key} className={`flex justify-between py-1.5 border-b border-[#FAF9F6]/10 ${bestStore?.key === s.key ? "text-[#B89C6A]" : ""}`}>
                                    <span className="text-[13px]">{s.label} {bestStore?.key === s.key && "★ le moins cher"}</span>
                                    <span className="font-serif-lux text-lg" data-testid={`total-${s.key}`}>{fmt(totals.t[s.key])} €</span>
                                </div>
                            ))}
                            {bestStore && (
                                <p className="mt-3 text-[11px] text-[#FAF9F6]/60">
                                    Économie vs le plus cher : <b className="text-[#B89C6A]">{fmt(Math.max(...stores.map((s) => totals.t[s.key])) - totals.t[bestStore.key])} €</b>
                                </p>
                            )}
                            <button
                                data-testid="materials-quote-open"
                                onClick={() => setQuoteOpen((o) => !o)}
                                className="mt-4 w-full inline-flex justify-center items-center px-5 py-3 bg-[#B89C6A] text-[#1C1E1D] text-[10px] font-medium tracking-[0.18em] uppercase hover:bg-[#FAF9F6] transition-colors"
                            >
                                Faire poser par CM RENOV →
                            </button>
                            {quoteOpen && (
                                <form onSubmit={submitQuoteRequest} className="mt-3 space-y-2" data-testid="materials-quote-form">
                                    <input
                                        data-testid="materials-quote-name"
                                        className="w-full bg-transparent border-b border-[#FAF9F6]/30 py-2 text-[13px] placeholder:text-[#FAF9F6]/40 focus:outline-none focus:border-[#B89C6A]"
                                        placeholder="Votre nom *" required
                                        value={quoteForm.full_name}
                                        onChange={(e) => setQuoteForm({ ...quoteForm, full_name: e.target.value })}
                                    />
                                    <input
                                        data-testid="materials-quote-email"
                                        type="email"
                                        className="w-full bg-transparent border-b border-[#FAF9F6]/30 py-2 text-[13px] placeholder:text-[#FAF9F6]/40 focus:outline-none focus:border-[#B89C6A]"
                                        placeholder="Votre email *" required
                                        value={quoteForm.email}
                                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                                    />
                                    <input
                                        data-testid="materials-quote-phone"
                                        className="w-full bg-transparent border-b border-[#FAF9F6]/30 py-2 text-[13px] placeholder:text-[#FAF9F6]/40 focus:outline-none focus:border-[#B89C6A]"
                                        placeholder="Téléphone"
                                        value={quoteForm.phone}
                                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                                    />
                                    <button
                                        data-testid="materials-quote-submit"
                                        type="submit" disabled={quoteBusy}
                                        className="w-full inline-flex justify-center items-center px-5 py-2.5 border border-[#B89C6A] text-[#B89C6A] text-[10px] font-medium tracking-[0.18em] uppercase hover:bg-[#B89C6A] hover:text-[#1C1E1D] transition-colors disabled:opacity-50"
                                    >
                                        {quoteBusy ? "Envoi…" : "Envoyer ma demande de devis"}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-8">
                    <div className="flex flex-wrap gap-2 mb-4">
                        <input
                            data-testid="materials-search"
                            className="lux-input flex-1 min-w-[200px]"
                            placeholder="Rechercher un matériau… (peinture, carrelage, câble…)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`materials-cat-${c}`}
                                onClick={() => setCat(c)}
                                className={`text-[10px] px-3 py-1.5 border tracking-wider uppercase transition ${cat === c ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    <div className="overflow-x-auto max-h-[440px] overflow-y-auto border hairline">
                        <table className="w-full text-[12px]">
                            <thead className="sticky top-0 bg-[#FAF9F6]">
                                <tr className="text-left">
                                    <th className="p-3 tracking-luxe text-[#B89C6A] text-[9px]">Matériau</th>
                                    {stores.map((s) => (
                                        <th key={s.key} className="p-3 tracking-luxe text-[#B89C6A] text-[9px] text-right whitespace-nowrap">{s.label}</th>
                                    ))}
                                    <th className="p-3 tracking-luxe text-[#B89C6A] text-[9px] text-right">Qté</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((m) => {
                                    const min = Math.min(...stores.map((s) => m.prices[s.key]));
                                    return (
                                        <tr key={m.id} className="border-t hairline hover:bg-[#F2EEE5]/60">
                                            <td className="p-3">
                                                <div>{m.name}</div>
                                                <div className="text-[10px] text-[#1C1E1D]/40">{m.category} · prix / {m.unit}</div>
                                            </td>
                                            {stores.map((s) => (
                                                <td key={s.key} className={`p-3 text-right whitespace-nowrap ${m.prices[s.key] === min ? "text-[#B89C6A] font-semibold" : "text-[#1C1E1D]/70"}`}>
                                                    {fmt(m.prices[s.key])} €
                                                </td>
                                            ))}
                                            <td className="p-3 text-right">
                                                <input
                                                    data-testid={`qty-${m.id}`}
                                                    type="number" min="0" max="999"
                                                    className="lux-input w-16 py-1 text-right"
                                                    value={qty[m.id] || ""}
                                                    placeholder="0"
                                                    onChange={(e) => setQty({ ...qty, [m.id]: e.target.value })}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={stores.length + 2} className="p-6 text-center text-[#1C1E1D]/40 italic">Aucun matériau trouvé.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
